/**
 * Shared runway lengths: OurAirports snapshot joined to Content.xml / Destinations ICAOs.
 *
 * Same numbers for Dispatch, Pilot, and Megan. Under 5000 ft is a PIC warning
 * only — never a hard assign / schedule reject.
 */
import icaoRunways from "./data/icaoRunways.json";
import { PIC_RUNWAY_WARN_FT, runwayPicPairWarning, runwayPicWarning } from "./hawker800xp";
import { safeTrim } from "./text";

export type RunwaySource = "catalog" | "ourairports" | "scenery" | "unknown";

export type AirportRunwayHit = {
  icao: string;
  longestFt: number;
  surface: string;
  source: RunwaySource;
};

export type RunwayResolveInput = {
  runwayFt?: number | null;
  notes?: string | null;
  catalogFt?: number | null;
};

/** JSON imports as unknown[] — validate at lookup. */
const TABLE = icaoRunways as Record<string, unknown>;

const SCAN_STUB_RE = /from community scan|starter placeholder|manual add — not in catalog/i;

export function icaoRunwayCount(): number {
  try {
    return Object.keys(TABLE).length;
  } catch {
    return 0;
  }
}

export function parseRunwayEntry(raw: unknown): { longestFt: number; surface: string } | null {
  if (Array.isArray(raw)) {
    const ft = Number(raw[0]);
    if (!Number.isFinite(ft) || ft <= 0) return null;
    const surface = typeof raw[1] === "string" ? raw[1].trim().toUpperCase() : "";
    return { longestFt: Math.round(ft), surface };
  }
  if (raw && typeof raw === "object") {
    const rec = raw as { longestFt?: unknown; ft?: unknown; surface?: unknown };
    const ft = Number(rec.longestFt ?? rec.ft);
    if (!Number.isFinite(ft) || ft <= 0) return null;
    const surface = typeof rec.surface === "string" ? rec.surface.trim().toUpperCase() : "";
    return { longestFt: Math.round(ft), surface };
  }
  const ft = Number(raw);
  if (!Number.isFinite(ft) || ft <= 0) return null;
  return { longestFt: Math.round(ft), surface: "" };
}

export function lookupOurAirportsRunway(
  icao?: string | null,
  table: Record<string, unknown> = TABLE,
): { longestFt: number; surface: string } | null {
  const code = safeTrim(icao).toUpperCase();
  if (!code) return null;
  try {
    return parseRunwayEntry(table[code]);
  } catch {
    return null;
  }
}

export function isScanStubRunway(existing?: RunwayResolveInput | null): boolean {
  if (!existing) return true;
  const notes = safeTrim(existing.notes);
  if (SCAN_STUB_RE.test(notes)) return true;
  const ft = Number(existing.runwayFt);
  if (!Number.isFinite(ft) || ft <= 0) return true;
  return false;
}

/**
 * Length for one ICAO. User/scenery non-stub wins; then the short AIRPORTS
 * catalog; then the OurAirports snapshot. Unknown is 0 — not a fake 5000 ft.
 */
export function resolveAirportRunway(
  icao?: string | null,
  existing?: RunwayResolveInput | null,
  table: Record<string, unknown> = TABLE,
): AirportRunwayHit {
  const code = safeTrim(icao).toUpperCase();
  const oa = lookupOurAirportsRunway(code, table);
  const catalogFt = Number(existing?.catalogFt);
  const hasCatalog = Number.isFinite(catalogFt) && catalogFt > 0;
  const sceneryFt = Number(existing?.runwayFt);
  const hasScenery = Number.isFinite(sceneryFt) && sceneryFt > 0;
  const stub = isScanStubRunway(existing);

  if (hasScenery && !stub && sceneryFt !== 5000) {
    return { icao: code, longestFt: Math.round(sceneryFt), surface: oa?.surface ?? "", source: "scenery" };
  }
  if (hasCatalog) {
    return { icao: code, longestFt: Math.round(catalogFt), surface: oa?.surface ?? "", source: "catalog" };
  }
  if (oa) {
    return { icao: code, longestFt: oa.longestFt, surface: oa.surface, source: "ourairports" };
  }
  if (hasScenery) {
    return { icao: code, longestFt: Math.round(sceneryFt), surface: "", source: "scenery" };
  }
  return { icao: code, longestFt: 0, surface: "", source: "unknown" };
}

/** Join a Content.xml / Community ICAO list to runway lengths. Missing rows stay source=unknown. */
export function joinIcaosToRunways(
  icaos: Iterable<string>,
  opts?: { existing?: Record<string, RunwayResolveInput>; table?: Record<string, unknown> },
): AirportRunwayHit[] {
  const seen = new Set<string>();
  const out: AirportRunwayHit[] = [];
  for (const raw of icaos) {
    const code = safeTrim(raw).toUpperCase();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push(resolveAirportRunway(code, opts?.existing?.[code], opts?.table ?? TABLE));
  }
  return out.sort((a, b) => a.icao.localeCompare(b.icao));
}

export function formatRunwayFt(ft: number | undefined | null): string {
  const n = Number(ft);
  if (!Number.isFinite(n) || n <= 0) return "unknown";
  return `${Math.round(n).toLocaleString("en-US")} ft`;
}

export function formatAirportRunway(hit: Pick<AirportRunwayHit, "icao" | "longestFt" | "surface">): string {
  const bits = [hit.icao, formatRunwayFt(hit.longestFt)];
  if (hit.surface) bits.push(hit.surface);
  return bits.join(" ");
}

export function formatTripRunwayBrief(
  origin: { icao: string; runwayFt?: number; surface?: string },
  dest: { icao: string; runwayFt?: number; surface?: string },
): { line: string; warning: string | null } {
  const o = { icao: origin.icao, longestFt: Number(origin.runwayFt) || 0, surface: origin.surface ?? "" };
  const d = { icao: dest.icao, longestFt: Number(dest.runwayFt) || 0, surface: dest.surface ?? "" };
  const line = `${formatAirportRunway(o)} · ${formatAirportRunway(d)}`;
  const warning = runwayPicPairWarning(
    { icao: origin.icao, runwayFt: o.longestFt },
    { icao: dest.icao, runwayFt: d.longestFt },
  );
  return { line, warning };
}

export function runwayWarnClass(runwayFt: number | undefined | null): "rwy-pic" | undefined {
  return runwayPicWarning(runwayFt) ? "rwy-pic" : undefined;
}

export { PIC_RUNWAY_WARN_FT, runwayPicWarning, runwayPicPairWarning };
